import React, { useMemo, useState } from 'react';
import { FileCheck2 } from 'lucide-react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useControllerIPEStatus } from '../../../hooks/analytics/useControllerReports.ts';
import { RefreshMeta, StatCard, StatusChip } from './shared.tsx';
import type { ControllerIPEStatusRow } from '../../../request/analytics/controllerReports.ts';

const ACTIVE_WORKFLOW_STATUSES = new Set(['in_progress', 'completed']);

/** Report #25 — IPE Validation Status. KPI tile (% IPE-validated) + filterable table. */
export const IPEStatusPanel: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerIPEStatus();
  const [nonValidatedOnly, setNonValidatedOnly] = useState(false);

  const scoped = useMemo(
    () => (controlIdFilter ? (data ?? []).filter((r) => controlIdFilter.has(r.control_id)) : data ?? []),
    [data, controlIdFilter]
  );

  const { validatedPct, validatedCount, totalCount } = useMemo(() => {
    const withRun = scoped.filter((r) => r.workflow_status != null);
    const validated = withRun.filter((r) => r.ipe_validated === true).length;
    return {
      validatedPct: withRun.length > 0 ? (validated / withRun.length) * 100 : null,
      validatedCount: validated,
      totalCount: withRun.length,
    };
  }, [scoped]);

  const filteredRows = useMemo(() => {
    return nonValidatedOnly ? scoped.filter((r) => r.ipe_validated !== true) : scoped;
  }, [scoped, nonValidatedOnly]);

  const columns = useMemo<TableColumnDef<ControllerIPEStatusRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">
            {row.original.control_id} — {row.original.title}
          </span>
        ),
        widthPercent: 44,
      },
      {
        id: 'ipe_validated',
        columnLabel: 'IPE Validated',
        header: () => 'IPE Validated',
        cell: ({ row }) => {
          const validated = row.original.ipe_validated;
          const flagged = validated !== true && ACTIVE_WORKFLOW_STATUSES.has(row.original.workflow_status ?? '');
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                validated === true
                  ? 'bg-green-100 text-green-700'
                  : flagged
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {validated === true ? 'Validated' : validated === false ? 'Not validated' : 'N/A'}
            </span>
          );
        },
        widthPercent: 28,
      },
      {
        id: 'workflow_status',
        columnLabel: 'Test Status',
        header: () => 'Test Status',
        cell: ({ row }) => <StatusChip value={row.original.workflow_status} />,
        widthPercent: 28,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <StatCard
        title="IPE-Validated Controls"
        value={validatedPct != null ? `${validatedPct.toFixed(0)}%` : '—'}
        subtitle={totalCount > 0 ? `${validatedCount} of ${totalCount} tested controls` : 'No tested controls yet'}
        icon={<FileCheck2 className="h-6 w-6" />}
        color="bg-teal-100 text-teal-600"
      />
      <AppDataTable<ControllerIPEStatusRow>
        data={filteredRows}
        columns={columns}
        loading={isLoading}
        error={isError ? error?.message ?? 'Failed to load IPE validation status.' : null}
        emptyMessage="No controls found."
        paginated
        defaultPageSize={8}
        headerStart={
          <div className="flex w-full items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={nonValidatedOnly}
                onChange={(e) => setNonValidatedOnly(e.target.checked)}
              />
              Non-validated only
            </label>
            <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
          </div>
        }
      />
    </div>
  );
};

export default IPEStatusPanel;
